import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  Creator,
  FamilyMember,
  FamilySpace,
  StoryCategory,
  StoryItem,
  StoryListingData,
  StoryListingRequest,
  StoryMasterData,
  StoryType,
} from '../../../../core/models/story.models';

/** Number of stories loaded per page (3-column grid × 3 rows). */
const PAGE_SIZE = 10;

/** LocalStorage key used to persist the user's selected view mode. */
const VIEW_MODE_KEY = 'fm_stories_view_mode';

@Component({
  selector: 'app-stories-list',
  templateUrl: './stories-list.html',
  styleUrl: './stories-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, FormsModule],
})
export class StoriesList implements OnInit, OnDestroy {

  // ── Dependency injection ───────────────────────────────────────────────────
  private readonly api        = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone       = inject(NgZone);
  private readonly route      = inject(ActivatedRoute);

  // ── UI state ───────────────────────────────────────────────────────────────
  /** True until the very first loadStories() call completes (suppresses premature empty states). */
  readonly isInitializing = signal(true);
  /** True when the stories API call returned a failure / network error. */
  readonly hasError = signal(false);

  /** Controls visibility of the expandable filter panel. */
  readonly isFilterPanelOpen = signal(false);

  // ── Server data ────────────────────────────────────────────────────────────

  /** Story cards returned by the current listing API call. */
  readonly stories = signal<StoryItem[]>([]);

  /** Total matching records across all pages (from API). */
  readonly totalRecords = signal(0);

  /** True when more stories remain beyond the currently loaded set. */
  readonly hasMoreStories = signal(false);

  /** True while an incremental (scroll-triggered) load is in progress. */
  readonly isLoadingMore = signal(false);

  /** True once any API call has returned totalRecords > 0 (used for empty-state discrimination). */
  readonly hasStoriesInSystem = signal(false);

  /** Category list from GET /Story/GetActiveCategories. */
  readonly categories = signal<StoryCategory[]>([]);

  /** Family groups the user belongs to — GET /Story/GetActiveFamilyList. */
  readonly familySpaces = signal<FamilySpace[]>([]);

  /** All family members — GET /Story/GetFamilyMemberList (for tagged-person filter). */
  readonly familyMembers = signal<FamilyMember[]>([]);

  /** All story creators — GET /Story/GetAllCreatorList. */
  readonly creators = signal<Creator[]>([]);

  // ── Active filter state ────────────────────────────────────────────────────

  /** Current value of the search input — two-way bound via [(ngModel)]. */
  customSearch = '';

  /**
   * The search term that was actually submitted (Enter / search button).
   * Drives the × clear button visibility, the API request, and hasActiveFilters.
   * Kept separate from customSearch so typing alone does not trigger a search.
   */
  readonly submittedSearch = signal('');

  /**
   * Selected category ID for the horizontal tag bar.
   * null  → All Stories (no category filter)
   */
  readonly activeCategoryId = signal<number | null>(null);

  /**
   * Selected story-visibility type for the dropdown.
   * null          → All (Own + Shared + Tagged)
   * StoryType.Own → 1, Shared → 2, Tagged → 3
   */
  readonly activeStoryType = signal<StoryType | null>(null);

  /**
   * Selected family-space UUID for the family filter.
   * null → All families
   */
  readonly activeFamilySpaceId = signal<string | null>(null);

  /** Selected tagged-person IDs (multi-select). Empty array = no filter. */
  readonly activeTaggedPersonIds = signal<string[]>([]);

  /** Selected creator IDs (multi-select). Empty array = no filter. */
  readonly activeCreatorIds = signal<string[]>([]);

  /** Story type filter options rendered in the filter panel. */
  readonly storyTypeOptions: { label: string; value: string }[] = [
    { label: 'All',    value: '' },
    { label: 'Own',    value: '1' },
    { label: 'Shared', value: '2' },
    { label: 'Tagged', value: '3' },
  ];

  /** Current page number (1-based). */
  readonly pageNumber = signal(1);

  // ── View mode — persisted via localStorage ─────────────────────────────────

  /** 'grid' | 'list' — default is grid; read from localStorage on init. */
  readonly viewMode = signal<'grid' | 'list'>(this.readViewMode());

  // ── Derived filter lists — scoped to the selected family ──────────────────

  /**
   * Family members visible in the "Filter by Tagged Person" panel.
   * When a family is selected, only members of that family are shown.
   */
  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.familyMembers();
    return this.familyMembers().filter(m => m.familySpaceId === familyId);
  });

  /**
   * Creators visible in the "Filter by Creator" panel.
   * When a family is selected, shows only creators belonging to that family.
   * First tries creator.familySpaceId (direct match); falls back to cross-referencing
   * familyMember.userId. If either approach yields no results, shows all creators.
   */
  readonly filteredCreators = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.creators();

    // Primary: filter directly by creator.familySpaceId if the API provides it.
    const byFamilySpace = this.creators().filter(c => c.familySpaceId === familyId);
    if (byFamilySpace.length > 0) return byFamilySpace;

    // Secondary: cross-reference via userId on family members.
    const familyUserIds = new Set(
      this.familyMembers()
        .filter(m => m.familySpaceId === familyId && !!m.userId)
        .map(m => m.userId as string),
    );
    if (familyUserIds.size > 0) {
      const byUserId = this.creators().filter(c => familyUserIds.has(c.userId));
      if (byUserId.length > 0) return byUserId;
    }

    // Fall back to all creators so the section is never blank.
    return this.creators();
  });

  // ── Private ────────────────────────────────────────────────────────────────

  /** Guard that prevents a second concurrent request from firing. */
  private isRequestInFlight = false;

  /** IntersectionObserver that fires lazy loads when the sentinel scrolls into view. */
  private scrollObserver?: IntersectionObserver;

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initPage();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Initialisation — load filters then first page of stories
  // ────────────────────────────────────────────────────────────────────────────

  private async initPage(): Promise<void> {
    // Single API call replaces the previous 4 parallel filter-data requests.
    await this.loadMasterData();

    // Pre-apply creator filter when navigating from the Loved Ones page.
    const creatorId = this.route.snapshot.queryParamMap.get('creatorId');
    if (creatorId) {
      this.activeCreatorIds.set([creatorId]);
      this.isFilterPanelOpen.set(true);
    }

    // Load stories only after filter data is ready.
    await this.loadStories();
  }

  /**
   * Fetches all filter-dropdown data in one request via GET /Story/GetStoryMasterData.
   * Maps the response fields to categories, familySpaces, familyMembers, and creators.
   */
  private async loadMasterData(): Promise<void> {
    const resp = await this.api.request<StoryMasterData>(
      'GET',
      API_ROUTES.GET_STORY_MASTER_DATA,
      undefined,
      { showToaster: false, showLoader: false },
    );
    if (resp.status && resp.data) {
      const d = resp.data;
      this.categories.set(d.categories ?? d.activeCategories ?? []);
      this.familySpaces.set(d.familySpaces ?? d.activeFamilyList ?? d.activeFamilies ?? []);
      this.familyMembers.set(d.familyMembers ?? d.familyMemberList ?? []);
      this.creators.set(d.creators ?? d.creatorList ?? d.allCreatorList ?? []);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Stories API call
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Fetches stories. When isLoadMore=false (default) it replaces the list
   * and re-attaches the scroll observer. When isLoadMore=true it appends.
   */
  async loadStories(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;
    if (isLoadMore && !this.hasMoreStories()) return;

    this.isRequestInFlight = true;

    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    const request: StoryListingRequest = {
      pageNumber:      this.pageNumber(),
      pageSize:        PAGE_SIZE,
      searchText:      this.submittedSearch(),
      categoryId:      this.activeCategoryId(),
      storyType:       this.activeStoryType(),
      familySpaceId:   this.activeFamilySpaceId(),
      creatorIds:      this.activeCreatorIds().length    ? this.activeCreatorIds()    : null,
      taggedPersonIds: this.activeTaggedPersonIds().length ? this.activeTaggedPersonIds() : null,
      sortColumn:      null,
      sortDirection:   null,
    };

    try {
      const resp = await this.api.request<StoryListingData>(
        'POST',
        API_ROUTES.GET_STORY_LISTING,
        request,
        { showToaster: false, showLoader: false, useToken: true },
      );

      if (resp.status && resp.data) {
        const incoming = resp.data.stories ?? [];
        const total    = resp.data.totalRecords ?? 0;

        if (isLoadMore) {
          this.stories.update(existing => [...existing, ...incoming]);
        } else {
          this.stories.set(incoming);
        }

        this.totalRecords.set(total);
        if (total > 0) this.hasStoriesInSystem.set(true);
        this.hasMoreStories.set(this.stories().length < total);
      } else {
        if (!isLoadMore) {
          this.stories.set([]);
          this.totalRecords.set(0);
          this.hasError.set(true);
        }
        this.hasMoreStories.set(false);
      }
    } catch {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.stories.set([]);
        this.totalRecords.set(0);
      }
      this.hasMoreStories.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);

      // Re-attach scroll observer after every fresh (page-1) load
      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }

  /** Attaches an IntersectionObserver to the bottom sentinel element. */
  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();

    const sentinel = document.querySelector('#storiesScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMoreStories()) {
          this.zone.run(() => {
            this.pageNumber.update(n => n + 1);
            this.loadStories(true);
          });
        }
      },
      { threshold: 0.1 },
    );

    this.scrollObserver.observe(sentinel);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────────

  /** Toggles the expandable filter panel open/closed. */
  toggleFilterPanel(): void {
    this.isFilterPanelOpen.update(open => !open);
  }

  /**
   * Fires the story listing API with the current search term.
   */
  async onSearch(): Promise<void> {
    const term = this.customSearch?.trim() ?? '';
    this.customSearch = term;
    this.submittedSearch.set(term);
    this.pageNumber.set(1);
    await this.loadStories();
  }

  /**
   * Clears the search input and reloads the story listing.
   */
  async clearSearch(): Promise<void> {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.pageNumber.set(1);
    await this.loadStories();
  }

  /**
   * Selects a category from the horizontal tag bar.
   * @param categoryId  null = All Stories, number = specific category
   */
  setCategory(categoryId: number | null): void {
    this.activeCategoryId.set(categoryId);
    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Handles the story-type filter change.
   * @param value  '' = All, '1' = Own, '2' = Shared, '3' = Tagged
   */
  onStoryTypeChange(value: string): void {
    this.activeStoryType.set(value === '' ? null : (Number(value) as StoryType));
    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Handles the family-space filter change.
   * @param value  '' = All families, otherwise a familySpaceId UUID
   */
  onFamilySpaceChange(value: string): void {
    const familyId = value === '' ? null : value;
    this.activeFamilySpaceId.set(familyId);

    if (familyId) {
      // Drop tagged-person selections not belonging to the newly selected family.
      const validMemberIds = new Set(
        this.familyMembers()
          .filter(m => m.familySpaceId === familyId)
          .map(m => m.familyMemberId),
      );
      this.activeTaggedPersonIds.update(ids => ids.filter(id => validMemberIds.has(id)));

      // Drop creator selections not belonging to the newly selected family.
      // Cross-references via userId on family members (mirrors filteredCreators logic).
      const familyUserIds = new Set(
        this.familyMembers()
          .filter(m => m.familySpaceId === familyId && !!m.userId)
          .map(m => m.userId as string),
      );
      if (familyUserIds.size > 0) {
        this.activeCreatorIds.update(ids => ids.filter(id => familyUserIds.has(id)));
      }
    }

    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Toggles a tagged-person ID in/out of the active filter set.
   */
  toggleTaggedPerson(id: string): void {
    this.activeTaggedPersonIds.update(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Toggles a creator ID in/out of the active filter set.
   */
  toggleCreator(id: string): void {
    this.activeCreatorIds.update(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Clears all active filters and reloads stories.
   */
  clearAllFilters(): void {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.activeCategoryId.set(null);
    this.activeStoryType.set(null);
    this.activeFamilySpaceId.set(null);
    this.activeTaggedPersonIds.set([]);
    this.activeCreatorIds.set([]);
    this.pageNumber.set(1);
    this.loadStories();
  }

  /**
   * Switches between grid and list view.
   * Persists the selection in localStorage so it survives page refresh.
   */
  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
    this.writeViewMode(mode);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Template utility helpers
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * True when any filter or search is active.
   * Used for empty-state discrimination in the template.
   */
  get hasAnyActiveFilter(): boolean {
    return (
      !!this.submittedSearch() ||
      this.activeCategoryId() !== null ||
      this.activeStoryType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      this.activeCreatorIds().length > 0
    );
  }

  /**
   * True when at least one panel filter (category / type / family) is active.
   * Excludes search — search has its own clear button in the search bar.
   * Used to control the Applied Filters panel visibility.
   * Creator IDs are only counted when a matching name exists in the creator list,
   * preventing a blank Applied Filters section from showing.
   */
  get hasActiveFilters(): boolean {
    const creatorSet = new Set(this.creators().map(c => c.userId));
    const hasValidCreator = this.activeCreatorIds().some(id => creatorSet.has(id));
    return (
      this.activeCategoryId() !== null ||
      this.activeStoryType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      hasValidCreator
    );
  }

  /** Returns the display name of the currently selected family space. */
  getActiveFamilyName(): string {
    const id = this.activeFamilySpaceId();
    if (!id) return '';
    return this.familySpaces().find(f => f.familySpaceId === id)?.familyName ?? '';
  }

  /** Returns the display name of the currently selected category. */
  getActiveCategoryName(): string {
    const id = this.activeCategoryId();
    if (id === null) return '';
    return this.categories().find(c => c.categoryId === id)?.categoryName ?? '';
  }

  /** Returns the display label for the currently selected story type filter. */
  getStoryTypeName(): string {
    switch (this.activeStoryType()) {
      case StoryType.Own:    return 'Own';
      case StoryType.Shared: return 'Shared';
      case StoryType.Tagged: return 'Tagged';
      default:               return '';
    }
  }

  /**
   * Returns up to 2 uppercase initials from a display name.
   * e.g. "John Doe" → "JD", "Alice" → "A"
   */
  getAuthorInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0].toUpperCase())
      .join('');
  }

  /**
   * Formats a duration value in seconds to "M:SS" display string.
   * e.g. 142 → "2:22" | 0 / null → "0:00"
   */
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /**
   * Converts an ISO date string to a human-readable relative time label.
   * e.g. "6 days ago", "1 week ago", "3 months ago"
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0)  return 'Today';
    if (diffDays === 1)  return '1 day ago';
    if (diffDays < 7)   return `${diffDays} days ago`;
    if (diffDays < 14)  return '1 week ago';
    if (diffDays < 30)  return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60)  return '1 month ago';
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    if (diffDays < 730) return '1 year ago';
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Private — localStorage helpers (SSR-safe via PLATFORM_ID)
  // ────────────────────────────────────────────────────────────────────────────

  private readViewMode(): 'grid' | 'list' {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      if (saved === 'grid' || saved === 'list') return saved;
    }
    return 'grid';
  }

  private writeViewMode(mode: 'grid' | 'list'): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    }
  }
}